"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { Modal } from "@/components/modal/modal";
import { IconButton } from "@/components/icon-button";
import CrossIcon from "@/assets/icons/cross";
import CircleCheckIcon from "@/assets/icons/circle-check";
import CartIcon from "@/assets/icons/cart";
import { priceSymbol } from "@/utils/price-count";
import ProductImage from "./ProductImage";

export interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productImage: string;
  quantity: number;
  price: number;
  freeShippingMessage?: string;
}

export const AddToCartModal = ({
  isOpen,
  onClose,
  productName,
  productImage,
  quantity,
  price,
  freeShippingMessage,
}: AddToCartModalProps) => {
  const router = useRouter();
  const lineTotal = price * quantity;

  useEffect(() => {
    if (isOpen) {
      console.log("[AddToCartModal] productImage URL:", productImage || "(empty)");
    }
  }, [isOpen, productImage]);

  const handleViewCart = () => {
    onClose();
    router.push("/cart");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      {/* Teal header */}
      <div className="bg-primary px-6 py-4 rounded-t-3xl xl:rounded-t-3xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-white flex items-center justify-center">
            <CircleCheckIcon size={24} color="white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-white text-lg">Added to Cart!</h3>
            <p className="text-white/90 text-sm">Item successfully added.</p>
          </div>
        </div>
        <IconButton
          rounded
          size="small"
          onClick={onClose}
          className="flex-shrink-0 text-white hover:bg-white/20"
        >
          <CrossIcon />
        </IconButton>
      </div>

      {/* Product info */}
      <div className="p-6 bg-white">
        <div className="flex gap-4 items-center">
          <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
            <ProductImage
              src={productImage}
              alt={productName}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 truncate">
              {productName}
            </p>
            <p className="text-gray-600 text-sm">Quantity: {quantity}</p>
            <p className="text-primary font-semibold mt-0.5">
              {priceSymbol(lineTotal)}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2 text-gray-600 text-sm">
          <div className="flex items-center gap-2">
            <CartIcon />
            <span>Your cart has been updated</span>
          </div>
          {freeShippingMessage && (
            <div className="flex items-center gap-2">
              <span>{freeShippingMessage}</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Continue Shopping
          </button>
          <button
            type="button"
            onClick={handleViewCart}
            className="flex-1 py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-cyan-600 transition-colors"
          >
            View Cart
          </button>
        </div>
      </div>
    </Modal>
  );
};
